import { HbsModal } from './hbs/Modal';

interface SafePasswordTipsModalProps {
  open: boolean;
  /** Called on Esc, backdrop click, or the "Got it" button. */
  onClose: () => void;
}

/**
 * Advice modal shown the first time a user focuses a new-safe-password
 * field. Combines the unrecoverable-loss warning with concrete guidance
 * on building a strong-and-memorable safe password.
 *
 * Shared by:
 *   - account creation (AuthPage)
 *   - change-safe-password (ChangePasswordModal)
 *   - forgot-password reset (ForgotPasswordPage)
 * so the copy stays identical wherever a user is setting their safe password.
 */
export function SafePasswordTipsModal({ open, onClose }: SafePasswordTipsModalProps): JSX.Element {
  return (
    <HbsModal
      open={open}
      onClose={onClose}
      title="Tips for a strong safe password"
      primaryAction={{ label: 'Got it', onClick: onClose }}
    >
      <p>
        If you forget your safe password, only a recovery contact within a
        Family you belong to can help you reset it. Please write it on paper
        and store it somewhere safe BEFORE you type it here.
      </p>
      <p className="font-semibold text-hbs-text">A good safe password is:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <span className="font-semibold text-hbs-text">Long</span> — length
          beats complexity. Aim for 16 characters or more.
        </li>
        <li>
          <span className="font-semibold text-hbs-text">Easy to remember</span>
          {' '}— a password you can't recall is a password you'll lose.
        </li>
      </ul>
      <p>
        <span className="font-semibold text-hbs-text">Try the phrase trick.</span>
        {' '}Start with a saying you'll remember easily and personalize it.
        For example, <em>This too shall pass</em> becomes{' '}
        <code className="font-mono text-hbs-text bg-hbs-surface-alt px-1.5 py-0.5 rounded">
          This2Sha!!Pa$$Tom
        </code>{' '}
        — if your name is Tom.
      </p>
    </HbsModal>
  );
}
